import { uniqueId } from 'lodash-es';
import { Observable, Subject, map, startWith } from 'rxjs';
import { shareReplayRefCount } from '../../../../../utils/rxjs';
import type {
    RuntimeDataServiceId,
    RuntimeDataServiceInput,
    RuntimeDataServiceOutput,
} from './contracts';
import type {
    ActiveBindingObserverConfig,
    ActiveDataBinding,
    BindingId,
    BindingInstanceKey,
    DebugActiveBindingDetails,
    RuntimeDataServiceInstance,
} from './types';
import { createBindingId } from './bindingCatalog';
import { extractResultLinkValueOrThrow, getResultLinkDescriptorOrThrow } from './resultLinks';

interface ActiveBindingRegistryDeps {
    requestData$<TServiceId extends RuntimeDataServiceId>(
        serviceId: TServiceId,
        input: RuntimeDataServiceInput<TServiceId>
    ): Observable<RuntimeDataServiceOutput<TServiceId>>;
}

interface ActiveBindingInputState {
    inputKey: string;
    input: RuntimeDataServiceInput<RuntimeDataServiceId>;
    stream$: Observable<unknown>;
    observers: Map<string, ActiveBindingObserverConfig>;
}

interface ActiveBindingEntryState {
    binding: ActiveDataBinding;
    bindingId: BindingId;
    bindingInstanceKey: BindingInstanceKey;
    inputStatesByKey: Map<string, ActiveBindingInputState>;
    changes$: Subject<void>;
    emitChange(): void;
}

function createBindingInstanceKey(binding: ActiveDataBinding): BindingInstanceKey {
    const normalizedArg = typeof binding.arg === 'undefined' ? null : binding.arg;
    return `${binding.serviceId}:${binding.linkKey}:${JSON.stringify(normalizedArg)}`;
}

function createInputKey(input: unknown): string {
    return JSON.stringify(input);
}

function createDeferredEmitter(subject$: Subject<void>): () => void {
    let changeScheduled = false;

    return () => {
        if (changeScheduled) {
            return;
        }

        // observeBinding(...) can be called from render paths. Defer debug
        // notifications to avoid React cross-component render-phase updates.
        changeScheduled = true;
        queueMicrotask(() => {
            changeScheduled = false;
            subject$.next();
        });
    };
}

interface ActiveBindingRegistryApi {
    observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        input: RuntimeDataServiceInput<TServiceId>,
        observer: ActiveBindingObserverConfig
    ): Observable<unknown>;
    observeActiveBindingInstanceKeys(): Observable<readonly BindingInstanceKey[]>;
    observeActiveBindingDebug(
        bindingInstanceKey: BindingInstanceKey
    ): Observable<DebugActiveBindingDetails>;
    observeActiveBindingInput(
        bindingInstanceKey: BindingInstanceKey,
        inputKey: string
    ): Observable<unknown>;
}

function buildActiveBindingDebug(entry: ActiveBindingEntryState): DebugActiveBindingDetails {
    return {
        bindingInstanceKey: entry.bindingInstanceKey,
        bindingId: entry.bindingId,
        binding: entry.binding,
        inputBuckets: Array.from(entry.inputStatesByKey.values()).map((inputState) => ({
            inputKey: inputState.inputKey,
            input: inputState.input,
            observerCount: inputState.observers.size,
            observers: Array.from(inputState.observers.values()),
        })),
    };
}

export function createActiveBindingRegistry(
    servicesById: Map<RuntimeDataServiceId, RuntimeDataServiceInstance>,
    deps: ActiveBindingRegistryDeps
): ActiveBindingRegistryApi {
    const entriesByKey = new Map<BindingInstanceKey, ActiveBindingEntryState>();
    const bindingInstanceKeysChanged$ = new Subject<void>();
    const emitBindingInstanceKeysChange = createDeferredEmitter(bindingInstanceKeysChanged$);
    const bindingInstanceKeys$ = bindingInstanceKeysChanged$.pipe(
        startWith(undefined),
        map(() => Array.from(entriesByKey.keys())),
        shareReplayRefCount()
    );

    function getOrCreateEntry(binding: ActiveDataBinding): ActiveBindingEntryState {
        const bindingInstanceKey = createBindingInstanceKey(binding);
        const existing = entriesByKey.get(bindingInstanceKey);
        if (existing) {
            return existing;
        }

        getResultLinkDescriptorOrThrow(servicesById, binding);

        const created: ActiveBindingEntryState = {
            binding: {
                serviceId: binding.serviceId,
                linkKey: binding.linkKey,
                ...(typeof binding.arg === 'undefined' ? {} : { arg: binding.arg }),
            },
            bindingId: createBindingId(binding),
            bindingInstanceKey,
            inputStatesByKey: new Map<string, ActiveBindingInputState>(),
            changes$: new Subject<void>(),
            emitChange: () => undefined,
        };
        created.emitChange = createDeferredEmitter(created.changes$);
        entriesByKey.set(bindingInstanceKey, created);
        emitBindingInstanceKeysChange();
        return created;
    }

    function getOrCreateInputState<TServiceId extends RuntimeDataServiceId>(
        entry: ActiveBindingEntryState,
        binding: ActiveDataBinding<TServiceId>,
        input: RuntimeDataServiceInput<TServiceId>
    ): ActiveBindingInputState {
        const inputKey = createInputKey(input);
        const existing = entry.inputStatesByKey.get(inputKey);
        if (existing) {
            return existing;
        }

        const stream$ = deps.requestData$(binding.serviceId, input).pipe(
            map((result) => extractResultLinkValueOrThrow(servicesById, binding, result)),
            shareReplayRefCount()
        );

        const created: ActiveBindingInputState = {
            inputKey,
            input,
            stream$,
            observers: new Map<string, ActiveBindingObserverConfig>(),
        };
        entry.inputStatesByKey.set(inputKey, created);
        entry.emitChange();
        return created;
    }

    function observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        input: RuntimeDataServiceInput<TServiceId>,
        observer: ActiveBindingObserverConfig
    ): Observable<unknown> {
        return new Observable<unknown>((subscriber) => {
            const entry = getOrCreateEntry(binding);
            const inputState = getOrCreateInputState(entry, binding, input);
            const observerId = uniqueId('active_binding_observer_');
            inputState.observers.set(observerId, observer);
            entry.emitChange();
            const subscription = inputState.stream$.subscribe({
                next(value) {
                    subscriber.next(value);
                },
                error(error) {
                    subscriber.error(error);
                },
                complete() {
                    // Keep the consumer attachment tracked until explicit unsubscribe.
                    // One-shot request streams complete quickly, but the UI consumer
                    // (e.g. table renderer) is still mounted and should remain visible
                    // in debug observer telemetry.
                },
            });

            return () => {
                subscription.unsubscribe();
                inputState.observers.delete(observerId);
                entry.emitChange();
                // TODO(reportbuilder): Add optional cooldown-based pruning for
                // inactive binding/input entries to avoid immediate teardown churn.
            };
        });
    }

    function observeActiveBindingInstanceKeys(): Observable<readonly BindingInstanceKey[]> {
        return bindingInstanceKeys$;
    }

    function observeActiveBindingDebug(
        bindingInstanceKey: BindingInstanceKey
    ): Observable<DebugActiveBindingDetails> {
        const entry = entriesByKey.get(bindingInstanceKey);
        if (!entry) {
            throw new Error(
                `Unknown active binding instance "${bindingInstanceKey}" for debug details.`
            );
        }

        return entry.changes$.pipe(
            startWith(undefined),
            map(() => buildActiveBindingDebug(entry)),
            shareReplayRefCount()
        );
    }

    function observeActiveBindingInput(
        bindingInstanceKey: BindingInstanceKey,
        inputKey: string
    ): Observable<unknown> {
        return new Observable<unknown>((subscriber) => {
            const entry = entriesByKey.get(bindingInstanceKey);
            const inputState = entry?.inputStatesByKey.get(inputKey);
            if (!inputState) {
                subscriber.error(
                    new Error(
                        `Unknown active binding input "${bindingInstanceKey}:${inputKey}" for debug preview.`
                    )
                );
                return undefined;
            }

            const subscription = inputState.stream$.subscribe(subscriber);
            return () => {
                subscription.unsubscribe();
            };
        });
    }

    return {
        observeBinding,
        observeActiveBindingInstanceKeys,
        observeActiveBindingDebug,
        observeActiveBindingInput,
    };
}
